import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Query, QueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, User } from 'lucide-react';
import { format } from 'date-fns';
import { localDateString, type ContainerRow, type Person } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import { addPersonFollowUp, deletePerson, getPerson, personFollowUps, updatePerson } from '../../services/people';
import { useContainers } from '../../services/containers';
import TaskItem from '../../components/TaskItem';
import { dangerButton, inputClass, outlineButton, primaryButton, tapLink } from '../../components/styles';

/** Refresh every list of people, but not this person's own query, which the caller has just set or removed. */
function refreshOtherPeople(queryClient: QueryClient, personId: string) {
  const own = queryKeys.person(personId);
  return queryClient.invalidateQueries({
    queryKey: queryKeys.people,
    predicate: (query: Query) => !own.every((part, i) => query.queryKey[i] === part),
  });
}

/** One person: see and edit their name, context and follow-ups, or delete them. */
export default function PersonDetailPage() {
  const { id = '' } = useParams();
  const personQuery = useQuery({ queryKey: queryKeys.person(id), queryFn: () => getPerson(id) });

  const back = (
    <Link to="/people" className={`${tapLink} text-sm text-primary hover:underline`}>
      ← People
    </Link>
  );
  if (personQuery.isError) {
    return (
      <div className="max-w-3xl mx-auto space-y-2">
        {back}
        <p className="text-sm text-error">Could not load this person: {errorMessage(personQuery.error)}</p>
      </div>
    );
  }
  if (personQuery.isPending) return <p className="text-sm text-on-surface-variant">Loading…</p>;
  if (!personQuery.data) {
    return (
      <div className="max-w-3xl mx-auto space-y-2">
        {back}
        <p className="text-on-surface-variant">This person was not found. They may have been deleted.</p>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {back}
      <PersonEditor key={personQuery.data.id} person={personQuery.data} />
    </div>
  );
}

function PersonEditor({ person }: { person: Person }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(person.name);
  const [context, setContext] = useState(person.context ?? '');
  const [validation, setValidation] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => updatePerson(person.id, { name, context: context.trim() || null }),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKeys.person(person.id), saved);
      return Promise.all([refreshOtherPeople(queryClient, person.id), queryClient.invalidateQueries({ queryKey: queryKeys.searches })]);
    },
  });

  const saved = mutation.data ?? person;
  const dirty =
    name.trim() !== saved.name ||
    (context.trim() || null) !== (saved.context ?? null);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setValidation('A person needs a name.');
      return;
    }
    setValidation(null);
    mutation.mutate();
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-primary-container flex items-center justify-center flex-shrink-0">
          <User className="w-6 h-6 text-primary" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl font-bold break-words">{saved.name}</h1>
          {saved.last_touched && (
            <p className="text-xs text-on-surface-variant">Last in touch {format(new Date(saved.last_touched), 'MMM d, yyyy')}</p>
          )}
        </div>
      </div>

      <form onSubmit={submit} className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
        <label className="block space-y-1">
          <span className="text-xs font-medium text-on-surface-variant">Name</span>
          <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" required />
        </label>
        <label className="block space-y-1">
          <span className="text-xs font-medium text-on-surface-variant">Context</span>
          <textarea
            rows={3}
            className={inputClass}
            value={context}
            onChange={(e) => setContext(e.target.value)}
            aria-label="Context"
            placeholder="How do you know this person?"
          />
        </label>

        {validation && <p className="text-sm text-error">{validation}</p>}
        {mutation.isError && <p className="text-sm text-error">Not saved: {errorMessage(mutation.error)}</p>}
        {dirty ? (
          <p className="text-xs text-on-surface-variant">Unsaved changes.</p>
        ) : (
          mutation.isSuccess && <p className="text-sm text-on-surface-variant">Saved.</p>
        )}

        <div className="flex gap-2">
          <button type="submit" disabled={mutation.isPending} className={primaryButton}>
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>

        <div className="pt-3 border-t border-outline/20">
          <DeletePerson person={saved} />
        </div>
      </form>

      <PersonFollowUps personId={person.id} />
    </>
  );
}

/** Delete a person after a confirm step. Only their links to tasks and notes go with them. */
function DeletePerson({ person }: { person: Person }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: () => deletePerson(person.id),
    onSuccess: () => {
      navigate('/people');
      // Their own query is gone and must not be fetched again.
      queryClient.removeQueries({ queryKey: queryKeys.person(person.id) });
      // Every list of people, and each note's people.
      return Promise.all([refreshOtherPeople(queryClient, person.id), queryClient.invalidateQueries({ queryKey: queryKeys.notes }), queryClient.invalidateQueries({ queryKey: queryKeys.searches })]);
    },
  });

  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} className={`${outlineButton} inline-flex items-center gap-1 text-error`}>
        <Trash2 className="w-4 h-4" aria-hidden="true" />
        Delete person…
      </button>
    );
  }
  return (
    <div role="group" aria-label="Delete this person" className="rounded-xl border border-error/30 bg-error/5 p-3 space-y-2">
      <p className="text-sm font-medium break-words">Delete “{person.name}”? This cannot be undone.</p>
      <p className="text-xs text-on-surface-variant">Tasks and notes that mention them stay; only the links to this person go.</p>
      {mutation.isError && <p className="text-sm text-error">Not deleted: {errorMessage(mutation.error)}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className={dangerButton}>
          {mutation.isPending ? 'Deleting…' : 'Delete person'}
        </button>
        <button type="button" onClick={() => setConfirming(false)} disabled={mutation.isPending} className={outlineButton}>
          Keep them
        </button>
      </div>
    </div>
  );
}

/** Where a new follow-up goes unless the person picks: the Home area, else the first area, else the first project. */
function defaultPlace(containers: readonly ContainerRow[]): ContainerRow | undefined {
  const active = containers.filter((c) => !c.archived_at && (c.kind === 'area' || c.kind === 'project'));
  return active.find((c) => c.kind === 'area' && c.name.trim().toLowerCase() === 'home') ?? active.find((c) => c.kind === 'area') ?? active[0];
}

/**
 * A person's follow-ups (CER-140): the open tasks linked to them, each a task
 * like any other, with its due date, reminder and place on Today. Adding one
 * files it as a next action linked to them.
 */
function PersonFollowUps({ personId }: { personId: string }) {
  const queryClient = useQueryClient();
  const today = localDateString();
  const followUps = useQuery({ queryKey: queryKeys.personFollowUps(personId), queryFn: () => personFollowUps(personId) });
  const containersQuery = useContainers();
  const containers = containersQuery.data ?? [];
  const places = containers.filter((c) => !c.archived_at && (c.kind === 'area' || c.kind === 'project'));
  const [words, setWords] = useState('');
  const [due, setDue] = useState('');
  const [placeId, setPlaceId] = useState('');
  const place = places.find((c) => c.id === placeId) ?? defaultPlace(containers);
  const add = useMutation({
    mutationFn: () => {
      if (!place) throw new Error('make a project or area first: a follow-up is a task, and a task lives in one');
      return addPersonFollowUp(personId, words, due || null, { kind: place.kind as 'project' | 'area', id: place.id });
    },
    onSuccess: () => {
      setWords('');
      setDue('');
      return Promise.all([queryKeys.tasks, queryKeys.people, queryKeys.captures].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (words.trim()) add.mutate();
  };

  return (
    <section aria-labelledby="follow-ups-heading" className="bg-surface rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
      <h2 id="follow-ups-heading" className="text-lg font-semibold">
        Follow-ups
      </h2>
      {followUps.isError ? (
        <p className="text-sm text-error">Follow-ups could not be loaded: {errorMessage(followUps.error)}</p>
      ) : followUps.data && followUps.data.length > 0 ? (
        <ul className="space-y-2">
          {followUps.data.map((task) => (
            <li key={task.id}>
              <TaskItem task={task} today={today} containers={containers} />
            </li>
          ))}
        </ul>
      ) : (
        followUps.isSuccess && <p className="text-sm text-on-surface-variant">No follow-ups.</p>
      )}
      <form onSubmit={submit} className="flex flex-wrap gap-2 items-end">
        <input
          className={`${inputClass} flex-1 min-w-48`}
          value={words}
          onChange={(e) => setWords(e.target.value)}
          aria-label="New follow-up"
          placeholder="Add a follow-up…"
        />
        <label className="text-xs text-on-surface-variant space-y-1">
          <span className="block">Due</span>
          <input type="date" className={inputClass} value={due} onChange={(e) => setDue(e.target.value)} aria-label="Follow-up due date" />
        </label>
        <label className="text-xs text-on-surface-variant space-y-1">
          <span className="block">Lives in</span>
          <select className={inputClass} value={place?.id ?? ''} onChange={(e) => setPlaceId(e.target.value)} aria-label="Follow-up place">
            {places.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!words.trim() || add.isPending} className={`${outlineButton} inline-flex items-center gap-1`}>
          <Plus className="w-4 h-4" aria-hidden="true" /> Add
        </button>
      </form>
      {add.isError && <p className="text-sm text-error">Not added: {errorMessage(add.error)}</p>}
    </section>
  );
}
